import React, { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  postChatMessageApiV3ChatPostMutation,
  getMessagesApiV3ConversationsConversationIdMessagesGetOptions,
  getMessagesApiV3ConversationsConversationIdMessagesGetQueryKey,
  getConversationsApiV3ConversationsGetQueryKey,
} from '../config/api/@tanstack/react-query.gen';
import type { MessageOut } from '../config/api/types.gen';

interface ChatContextType {
  conversationId: string | undefined;
  messages: MessageOut[];
  isLoading: boolean;
  isLoadingMessages: boolean;
  error: Error | null;
  sendMessage: (content: string) => void;
  selectConversation: (id: string) => void;
  startNewConversation: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

// Cache helpers
function appendToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  convId: string,
  msg: MessageOut,
) {
  const queryKey = getMessagesApiV3ConversationsConversationIdMessagesGetQueryKey({
    path: { conversation_id: convId },
  });
  queryClient.setQueryData<MessageOut[]>(queryKey, (old) => [...(old ?? []), msg]);
}

function seedCache(
  queryClient: ReturnType<typeof useQueryClient>,
  convId: string,
  msgs: MessageOut[],
) {
  const queryKey = getMessagesApiV3ConversationsConversationIdMessagesGetQueryKey({
    path: { conversation_id: convId },
  });
  queryClient.setQueryData<MessageOut[]>(queryKey, msgs);
}

function removeFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  convId: string,
  msgId: string,
) {
  const queryKey = getMessagesApiV3ConversationsConversationIdMessagesGetQueryKey({
    path: { conversation_id: convId },
  });
  queryClient.setQueryData<MessageOut[]>(queryKey, (old) =>
    (old ?? []).filter((m) => m.id !== msgId),
  );
}

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Initialize conversationId from URL on mount (lazy useState — no effect needed)
  const [conversationId, setConversationId] = useState<string | undefined>(() => {
    const match = location.pathname.match(/^\/chat\/([^/]+)$/);
    return match?.[1];
  });

  // Pending messages for new conversations (before API returns an ID)
  const [pendingMessages, setPendingMessages] = useState<MessageOut[]>([]);

  // Fetch existing messages when we have a conversation ID
  const messagesQuery = useQuery({
    ...getMessagesApiV3ConversationsConversationIdMessagesGetOptions({
      path: { conversation_id: conversationId! },
    }),
    enabled: !!conversationId,
  });

  // Derived messages: from react-query cache (existing conv) or pendingMessages (new conv)
  const messages = useMemo(
    () => (conversationId ? (messagesQuery.data ?? []) : pendingMessages),
    [conversationId, messagesQuery.data, pendingMessages],
  );

  const mutation = useMutation({
    ...postChatMessageApiV3ChatPostMutation(),
  });

  const sendMessage = useCallback(
    (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || mutation.isPending) return;

      // Build optimistic user message
      const userMsg: MessageOut = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: trimmed,
        created_at: new Date().toISOString(),
      };

      // Optimistic update
      if (conversationId) {
        appendToCache(queryClient, conversationId, userMsg);
      } else {
        setPendingMessages([userMsg]);
      }

      mutation.mutate(
        {
          body: {
            query: trimmed,
            conversation_id: conversationId ?? undefined,
          },
        },
        {
          onSuccess: (response) => {
            const assistantMsg: MessageOut = {
              id: `msg-${Date.now()}-assistant`,
              role: 'assistant',
              content: response.content,
              created_at: new Date().toISOString(),
            };

            if (conversationId) {
              // Existing conversation — append assistant message to cache
              appendToCache(queryClient, conversationId, assistantMsg);
            } else {
              // New conversation — seed the cache with both messages
              const newId = response.conversation_id;
              seedCache(queryClient, newId, [userMsg, assistantMsg]);
              setPendingMessages([]);
              setConversationId(newId);
              navigate(`/chat/${newId}`, { replace: true });
            }

            // Refresh sidebar conversations list
            queryClient.invalidateQueries({
              queryKey: getConversationsApiV3ConversationsGetQueryKey(),
            });
          },
          onError: () => {
            // Remove optimistic user message
            if (conversationId) {
              removeFromCache(queryClient, conversationId, userMsg.id);
            } else {
              setPendingMessages([]);
            }
          },
        },
      );
    },
    [conversationId, mutation, queryClient, navigate],
  );

  const selectConversation = useCallback(
    (id: string) => {
      setConversationId(id);
      setPendingMessages([]);
      navigate(`/chat/${id}`, { replace: true });
    },
    [navigate],
  );

  const startNewConversation = useCallback(() => {
    setConversationId(undefined);
    setPendingMessages([]);
    navigate('/chat', { replace: true });
  }, [navigate]);

  const value = useMemo<ChatContextType>(
    () => ({
      conversationId,
      messages,
      isLoading: mutation.isPending,
      isLoadingMessages: messagesQuery.isLoading && !!conversationId,
      error: mutation.error,
      sendMessage,
      selectConversation,
      startNewConversation,
    }),
    [
      conversationId,
      messages,
      mutation.isPending,
      mutation.error,
      messagesQuery.isLoading,
      sendMessage,
      selectConversation,
      startNewConversation,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
